import styled from 'styled-components';

import { Text } from '@/nova/components';
import { hoverWash } from '@/nova/theme/mixins';
import { COLORS } from '@/nova/theme/tokens';
import { CUST, CUST_ANIM, CUST_GRADIENTS } from '../../../../theme/customizer-tokens';

/* ── Desktop ─────────────────────────────────────────────────────────────── */

export const DesktopBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

/** Name cards: 3-up at full width, but never crush them into tall, unreadable
 *  slivers. As the content area narrows (a wide progress panel + the side rail
 *  eat the room) the grid reflows to 2 — then 1 — columns instead of keeping 3. */
export const NameGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
  width: 100%;
`;

export const DomainList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

/** Body copy for the "More" reveal modal (the full name's rationale). Lives in
 *  the shared `Overlay`, so it uses the standard studio text token, not `CUST`. */
export const ModalBodyText = styled(Text)`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${COLORS.featureName};
  white-space: pre-line;
`;

/* ── Mobile ──────────────────────────────────────────────────────────────── */

export const MobileBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

export const MobileHeading = styled(Text)`
  margin-top: 4px;
  font-size: 16px;
  font-weight: 900;
  line-height: 1.1;
  text-align: center;
  background-image: ${CUST_GRADIENTS.headingGradient};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
`;

/** Centered "Refresh suggestions" link under the mobile heading. */
export const MobileRefresh = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${CUST.accentText};
  cursor: pointer;
`;

export const MobileRefreshLabel = styled(Text)`
  font-size: 13.5px;
  line-height: 18px;
  color: ${CUST.accentText};
`;

export const MobileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  margin-top: 8px;
`;

export const MobileSubtext = styled(Text)`
  max-width: 320px;
  font-size: 13px;
  line-height: 1.4;
  text-align: center;
  color: ${CUST.textMuted};
`;

/* ── Manual subflow ("I have my own name") ───────────────────────────────── */

/** "⚡ I want to decide fast" — muted text toggle back to the AI subflow. */
export const DecideFastBtn = styled.button`
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 12px 0;
  border: none;
  background: transparent;
  color: ${CUST.textMuted};
  cursor: pointer;
  transition: filter ${CUST_ANIM.INTERACT_MS}ms ease;

  &:hover {
    filter: brightness(1.25);
  }
`;

export const DecideFastLabel = styled(Text)`
  font-size: 14px;
  line-height: 20px;
  color: ${CUST.textMuted};
  white-space: nowrap;
`;

/** "I have my own domain" + orange Connect-domain button (domain-connect wired later). */
export const ConnectRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 16px 24px;
  border: 1px solid transparent;
  border-radius: 16px;
  background: ${CUST.cardSurface};
`;

export const ConnectInfo = styled(Text)`
  min-width: 0;
  font-size: 16px;
  line-height: 1.2;
  color: ${CUST.textMuted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ConnectBtn = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  border: none;
  border-radius: 9999px;
  background: ${CUST.accent};
  color: ${CUST.onAccent};
  cursor: pointer;

  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${hoverWash}
`;

export const ConnectBtnLabel = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
`;